import React, { useState, useEffect } from "react";
import api, { formatVND } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  User, Phone, MapPin, ShieldCheck, Calendar, Clock, CheckCircle2,
  XCircle, Loader2, History, CreditCard, Edit, Save, X, BadgeCheck, CircleDollarSign, Image as ImageIcon, Upload,
} from "lucide-react";
import PhoneVerifyDialog from "@/components/PhoneVerifyDialog";
import PaymentReceiptDialog from "@/components/PaymentReceiptDialog";

const STATUS_MAP = {
  pending: { label: "Chờ xác nhận", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/25" },
  confirmed: { label: "Đã xác nhận", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/25" },
  completed: { label: "Hoàn thành", color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/25" },
  cancelled: { label: "Đã huỷ", color: "text-red-400", bg: "bg-red-500/10 border-red-500/25" },
};

export default function CustomerProfilePage() {
  const { user, updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);
  const [phoneVerifyOpen, setPhoneVerifyOpen] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [activeTab, setActiveTab] = useState("bookings"); // bookings | payments
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptBookingId, setReceiptBookingId] = useState(null);
  const [viewReceiptUrl, setViewReceiptUrl] = useState(null);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setAddress(user.address || "");
    }
  }, [user]);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    setLoadingBookings(true);
    try {
      const [bookingRes, meRes] = await Promise.all([
        api.get("/customer/my-bookings"),
        api.get("/auth/me"),
      ]);
      setBookings(bookingRes.data.bookings || []);
      if (meRes.data.user) updateUser(meRes.data.user);
    } catch { }
    finally { setLoadingBookings(false); }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", { name, address });
      updateUser(data.user);
      setEditing(false);
      toast.success("Cập nhật thông tin thành công");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi cập nhật");
    } finally { setSaving(false); }
  };

  const handlePhoneVerified = (updatedUser) => {
    updateUser(updatedUser);
    toast.success("Xác nhận SĐT thành công!");
  };

  const handleConfirmPayment = (bookingId) => {
    setReceiptBookingId(bookingId);
    setReceiptDialogOpen(true);
  };

  const phoneVerified = user?.phone_verified || false;

  // Split bookings by status
  const activeBookings = bookings.filter(b => b.status === "pending" || b.status === "confirmed");
  const pastBookings = bookings.filter(b => b.status === "completed" || b.status === "cancelled");

  // Payment history from bookings that are confirmed/completed with total_price > 0
  const payments = bookings.filter(b => b.total_price > 0 && (b.status === "confirmed" || b.status === "completed"));

  return (
    <div className="space-y-5 animate-fade-in-up max-w-3xl mx-auto" data-testid="customer-profile-page">
      {/* Profile card */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
        <div className="h-20 bg-gradient-to-r from-teal-600/30 to-sky-600/20" />
        <div className="px-5 pb-5 -mt-8">
          <div className="flex items-end gap-4">
            <div className="w-16 h-16 rounded-full bg-[#0f172a] border-4 border-[#1e293b] flex items-center justify-center">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <User className="w-7 h-7 text-teal-400" />
              )}
            </div>
            <div className="flex-1 mb-1">
              <h2 className="font-heading text-lg font-bold text-[#F8FAFC]">{user?.name || "Khách hàng"}</h2>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
            {!editing ? (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}
                className="border-[#334155] text-slate-300 gap-1 mb-1" data-testid="edit-profile-btn">
                <Edit className="w-3.5 h-3.5" /> Chỉnh sửa
              </Button>
            ) : (
              <div className="flex gap-1.5 mb-1">
                <Button size="sm" onClick={handleSaveProfile} disabled={saving}
                  className="bg-teal-500 hover:bg-teal-600 text-[#0f172a] font-semibold gap-1" data-testid="save-profile-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Lưu
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setName(user?.name || ""); setAddress(user?.address || ""); }}
                  className="text-slate-400"><X className="w-3.5 h-3.5" /></Button>
              </div>
            )}
          </div>

          {/* Profile fields */}
          <div className="mt-5 space-y-3">
            {/* Name */}
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-slate-500 shrink-0" />
              {editing ? (
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Họ tên"
                  className="bg-[#0f172a] border-[#334155] text-slate-200 h-9" data-testid="profile-name" />
              ) : (
                <span className="text-sm text-slate-200">{user?.name || "—"}</span>
              )}
            </div>

            {/* Phone */}
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-slate-500 shrink-0" />
              <div className="flex items-center gap-2 flex-1">
                <span className="text-sm text-slate-200">{user?.phone || "Chưa có SĐT"}</span>
                {user?.phone && phoneVerified && (
                  <span className="flex items-center gap-1 text-[10px] text-teal-400 bg-teal-500/10 border border-teal-500/20 rounded-full px-2 py-0.5" data-testid="phone-verified-badge">
                    <ShieldCheck className="w-3 h-3" /> Đã xác thực
                  </span>
                )}
                {user?.phone && !phoneVerified && (
                  <span className="text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-full px-2 py-0.5">Chưa xác thực</span>
                )}
              </div>
              <button onClick={() => setPhoneVerifyOpen(true)}
                className="text-[10px] text-teal-400 hover:text-teal-300 underline shrink-0" data-testid="verify-phone-btn">
                {user?.phone ? "Đổi SĐT" : "Thêm SĐT"}
              </button>
            </div>

            {/* Address */}
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
              {editing ? (
                <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Địa chỉ"
                  className="bg-[#0f172a] border-[#334155] text-slate-200 h-9" data-testid="profile-address" />
              ) : (
                <span className="text-sm text-slate-300">{user?.address || "Chưa cập nhật"}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Active bookings (current) */}
      {activeBookings.length > 0 && (
        <div className="bg-[#1e293b] border border-teal-500/20 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-semibold text-[#F8FAFC] flex items-center gap-2">
            <Calendar className="w-4 h-4 text-teal-400" /> Đặt sân hiện tại
          </h3>
          {activeBookings.map(b => {
            const st = STATUS_MAP[b.status] || STATUS_MAP.pending;
            const createdAtStr = b.created_at ? (() => { const d = new Date(b.created_at); return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")} ${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}`; })() : "";
            return (
              <div key={b.id} className={`${st.bg} border rounded-lg p-3`} data-testid={`active-booking-${b.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-sm font-bold text-slate-100">{b.court_name}</span>
                      <span className={`text-[10px] font-semibold ${st.color}`}>{st.label}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {b.date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {b.start_time}-{b.end_time}</span>
                    </div>
                    {createdAtStr && <p className="text-[10px] text-slate-400 mt-0.5">Đặt lúc: {createdAtStr}</p>}
                    {b.created_by_role && b.created_by_role !== "customer" && b.created_by_name && (
                      <p className="text-[10px] text-amber-300 mt-0.5 flex items-center gap-1" data-testid={`booked-by-staff-active-${b.id}`}>
                        <User className="w-3 h-3" /> Nhân viên đặt hộ: <span className="font-semibold">{b.created_by_name}</span>
                      </p>
                    )}
                    {b.cancel_reason && <p className="text-[11px] text-red-400 mt-1">Lý do: {b.cancel_reason}</p>}
                  </div>
                  <div className="text-right space-y-1.5 shrink-0 ml-3">
                    <span className="text-base font-bold text-lime-400 block">{formatVND(b.total_price)}đ</span>

                    {/* Payment status badges */}
                    {b.payment_verified_at ? (
                      <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full px-2 py-0.5 font-medium">
                        <BadgeCheck className="w-3 h-3" /> Đã XN thanh toán
                      </span>
                    ) : b.payment_confirmed_at ? (
                      <span className="inline-flex items-center gap-1 text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full px-2 py-0.5 font-medium">
                        <CircleDollarSign className="w-3 h-3" /> Đang chờ XN thanh toán
                      </span>
                    ) : b.status === "confirmed" ? (
                      <button onClick={() => handleConfirmPayment(b.id)}
                        className="text-[10px] bg-sky-500 hover:bg-sky-600 text-white font-semibold px-2.5 py-1 rounded-md flex items-center gap-1 ml-auto"
                        data-testid={`confirm-payment-${b.id}`}>
                        <CreditCard className="w-3 h-3" /> Thông tin chuyển khoản
                      </button>
                    ) : null}

                    {/* View receipt images */}
                    {((b.receipt_images && b.receipt_images.length > 0) || b.receipt_image_url) && (
                      <button onClick={() => {
                        const url = b.receipt_images?.length > 0
                          ? b.receipt_images[b.receipt_images.length - 1].url
                          : b.receipt_image_url;
                        if (url) setViewReceiptUrl(url);
                      }}
                        className="text-[10px] text-sky-400 hover:text-sky-300 flex items-center gap-1 ml-auto transition-colors"
                        data-testid={`view-receipt-${b.id}`}>
                        <ImageIcon className="w-3 h-3" /> Xem biên nhận {b.receipt_images?.length > 0 ? `(${b.receipt_images.length})` : ""}
                      </button>
                    )}

                    {/* Re-upload receipt */}
                    {b.payment_confirmed_at && !b.payment_verified_at && (
                      <button onClick={() => handleConfirmPayment(b.id)}
                        className="text-[10px] text-slate-500 hover:text-sky-400 flex items-center gap-1 ml-auto transition-colors"
                        data-testid={`reupload-receipt-${b.id}`}>
                        <Upload className="w-3 h-3" /> Tải lại biên nhận
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs: History + Payments */}
      <div className="flex gap-1 bg-[#1e293b] rounded-lg p-1 border border-[#334155]">
        <button onClick={() => setActiveTab("bookings")}
          className={`flex-1 py-2 rounded-md text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${activeTab === "bookings" ? "bg-teal-500/20 text-teal-400" : "text-slate-400 hover:text-slate-200"}`}
          data-testid="tab-booking-history">
          <History className="w-3.5 h-3.5" /> Lịch sử đặt sân
        </button>
        <button onClick={() => setActiveTab("payments")}
          className={`flex-1 py-2 rounded-md text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${activeTab === "payments" ? "bg-sky-500/20 text-sky-400" : "text-slate-400 hover:text-slate-200"}`}
          data-testid="tab-payment-history">
          <CreditCard className="w-3.5 h-3.5" /> Lịch sử thanh toán
        </button>
      </div>

      {/* Tab content */}
      {activeTab === "bookings" && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 space-y-2" data-testid="booking-history-list">
          {loadingBookings ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-teal-500" /></div>
          ) : bookings.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-6">Chưa có lịch sử đặt sân</p>
          ) : (
            bookings.map(b => {
              const st = STATUS_MAP[b.status] || STATUS_MAP.pending;
              const createdAtStr = b.created_at ? (() => { const d = new Date(b.created_at); return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")} ${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}`; })() : "";
              return (
                <div key={b.id} className="bg-[#0f172a] border border-[#334155] rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3 text-teal-400" />
                        <span className="text-sm font-semibold text-slate-200">{b.court_name}</span>
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${st.bg} ${st.color}`}>{st.label}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {b.date}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {b.start_time}-{b.end_time}</span>
                      </div>
                      {createdAtStr && <p className="text-[10px] text-slate-400 mt-0.5">Đặt lúc: {createdAtStr}</p>}
                      {b.created_by_role && b.created_by_role !== "customer" && b.created_by_name && (
                        <p className="text-[10px] text-amber-300 mt-0.5 flex items-center gap-1" data-testid={`booked-by-staff-history-${b.id}`}>
                          <User className="w-3 h-3" /> Đặt hộ bởi: <span className="font-semibold">{b.created_by_name}</span>
                        </p>
                      )}
                    </div>
                    <div className="text-right space-y-1">
                      <span className="text-sm font-bold text-lime-400">{formatVND(b.total_price)}đ</span>
                      {b.payment_verified_at && (
                        <span className="flex items-center gap-1 text-[9px] text-emerald-400 justify-end"><BadgeCheck className="w-3 h-3" /> Đã XN TT</span>
                      )}
                      {b.payment_confirmed_at && !b.payment_verified_at && (
                        <span className="flex items-center gap-1 text-[9px] text-amber-400 justify-end"><CircleDollarSign className="w-3 h-3" /> Chờ XN TT</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {activeTab === "payments" && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 space-y-2" data-testid="payment-history-list">
          {payments.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-6">Chưa có lịch sử thanh toán</p>
          ) : (
            <>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-slate-500">Tổng cộng</span>
                <span className="text-base font-bold text-lime-400">{formatVND(payments.reduce((s, b) => s + (b.total_price || 0), 0))}đ</span>
              </div>
              {payments.map(b => (
                <div key={b.id} className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-3 h-3 text-sky-400" />
                      <span className="text-sm text-slate-200">{b.court_name}</span>
                    </div>
                    <span className="text-[11px] text-slate-500">{b.date} | {b.start_time}-{b.end_time}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-lime-400">{formatVND(b.total_price)}đ</span>
                    <span className={`block text-[9px] ${(STATUS_MAP[b.status] || STATUS_MAP.pending).color}`}>
                      {b.status === "confirmed" ? "Chờ thanh toán" : "Đã thanh toán"}
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      <PhoneVerifyDialog open={phoneVerifyOpen} onOpenChange={setPhoneVerifyOpen} onVerified={handlePhoneVerified} />
      <PaymentReceiptDialog
        open={receiptDialogOpen}
        onOpenChange={setReceiptDialogOpen}
        bookingId={receiptBookingId}
        onSuccess={loadBookings}
      />

      {/* Receipt image viewer */}
      {viewReceiptUrl && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4" style={{ zIndex: 9999 }}
          onClick={() => setViewReceiptUrl(null)} data-testid="receipt-viewer-overlay">
          <div className="relative max-w-lg max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <img src={viewReceiptUrl} alt="Biên nhận" className="max-w-full max-h-[80vh] rounded-lg object-contain border border-[#334155]" />
            <button onClick={() => setViewReceiptUrl(null)}
              className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-colors"
              data-testid="close-receipt-viewer">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
